import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase-config";
import { collection, doc, getDoc, setDoc, onSnapshot, query, where } from "firebase/firestore";
import {
  Gem,
  BarChart3,
  Users,
  Trophy,
  ArrowRight,
  Music,
  Music2,
  HelpCircle,
  Search,
  Sparkles,
  Timer
} from "lucide-react";
import { GameOnboarding } from "./GameOnboarding";
import { TeamCreation } from "./TeamCreation";
import { TeamInfo } from "./TeamInfo";
import { GameRounds } from "./GameRounds";
import { AdditionalGameRounds } from "./AdditionalGameRounds";
import { GameLeaderboards } from "./GameLeaderboards";
import { ShowdownAnswers } from "./ShowdownAnswers";
import { PixelPuzzler } from "./PixelPuzzler";
import { NameThatLyric } from "./NameThatLyric";
import { NameThatTune } from "./NameThatTune";
import { initializeQuizData } from "../utils/initQuizData";
import "./JukeboxShowdownGame.scss";

export const JukeboxShowdownGame = ({ userObj, clientObj, setTab, jukebox }) => {
  const [activeView, setActiveView] = useState('home');
  const [userDiamonds, setUserDiamonds] = useState(12000);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [userTeam, setUserTeam] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [completedGameScores, setCompletedGameScores] = useState(null);
  const [completedGameId, setCompletedGameId] = useState(null);

  useEffect(() => {
    // Check if user is anonymous
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      setIsAnonymous(true);
      // Redirect to profile for anonymous users
      if (setTab) {
        setTab("profile");
      }
    }
  }, [setTab]);

  // Fetch user's teams from root teams collection
  useEffect(() => {
    if (!auth.currentUser || !clientObj?.clientId) {
      setLoadingTeam(false);
      return;
    }

    const fetchTeams = async () => {
      setLoadingTeam(true);
      
      // First fetch the actual gameId from the client's document
      let gameId = "default";
      try {
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.currentGameId || "default";
          console.log("JukeboxShowdown - Fetched currentGameId from client:", gameId);
        } else {
          console.warn("JukeboxShowdown - Client document not found, using default gameId");
        }
      } catch (error) {
        console.error("JukeboxShowdown - Error fetching client document:", error);
      }
      
      // Query teams from root collection filtered by gameId
      const teamsRef = collection(db, "teams");
      const teamsQuery = query(teamsRef, where("gameId", "==", gameId));
      
      const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
        let userTeamFound = null;
        
        // Find team where current user is a member
        snapshot.forEach((doc) => {
          const teamData = doc.data();
          if (teamData.teamMembers && 
              teamData.teamMembers.some(member => member.id === auth.currentUser.uid)) {
            userTeamFound = {
              id: doc.id,
              ...teamData
            };
          }
        });
        
        setUserTeam(userTeamFound);
        setLoadingTeam(false);
      }, (error) => {
        console.error("Error fetching teams:", error);
        setUserTeam(null);
        setLoadingTeam(false);
      });

      return unsubscribe;
    };

    fetchTeams();
  }, [clientObj]);

  // Check for game completion
  useEffect(() => {
    if (!userTeam?.id || !clientObj?.clientId) {
      return;
    }

    const checkGameCompletion = async () => {
      try {
        // Get the gameId from client document
        let gameId = "default";
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.gameId || clientData.game_id || "default";
        }

        // Clear scores if game changed
        if (completedGameId && completedGameId !== gameId) {
          setGameCompleted(false);
          setCompletedGameScores(null);
          setCompletedGameId(null);
          return;
        }

        // Listen to team scores document for completion
        const teamScoreRef = doc(db, "users", clientObj.clientId, "games", gameId, "teamScores", userTeam.id);
        const unsubscribe = onSnapshot(teamScoreRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            if (data.gameCompleted) {
              setGameCompleted(true);
              setCompletedGameScores({
                teamScore: data.totalScore || 0,
                teamName: data.teamName || userTeam.teamName,
                members: data.members || {}
              });
              setCompletedGameId(gameId);
            }
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error("Error checking game completion:", error);
      }
    };

    checkGameCompletion();
  }, [userTeam, clientObj, completedGameId]);

  const handleTeamDeleted = () => {
    setUserTeam(null);
    setActiveView('home');
  };

  const handleTeamCreated = () => {
    setActiveView('home');
  };

  // Check if team member has confirmed their invitation
  const checkMemberConfirmed = async (teamId, memberId) => {
    try {
      // Get the gameId from client document
      let gameId = "default";
      if (clientObj?.clientId) {
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.gameId || clientData.game_id || "default";
        }
      }
      
      // Check the member's ready status in games document
      const gamesDocRef = doc(db, "users", clientObj.clientId, "games", gameId);
      const gamesDoc = await getDoc(gamesDocRef);
      
      if (gamesDoc.exists()) {
        const gamesData = gamesDoc.data();
        const teams = gamesData.teams || [];
        const team = teams.find(t => t.teamId === teamId);
        
        if (team && team.teamMembers) {
          const member = team.teamMembers.find(m => m.id === memberId);
          return member?.ready === true;
        }
      }
      
      return false;
    } catch (error) {
      console.error("Error checking member confirmation:", error);
      return false;
    }
  };

  // Check if game has started by checking if team has scores
  const checkGameStarted = async (teamId) => {
    try {
      // Get the gameId from client document
      let gameId = "default";
      if (clientObj?.clientId) {
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.gameId || clientData.game_id || "default";
        }
      }
      
      // Check if team scores exist - if they do, game has started
      const teamScoreRef = doc(db, "users", clientObj.clientId, "games", gameId, "teamScores", teamId);
      const teamScoreDoc = await getDoc(teamScoreRef);
      
      // If scores exist, the game has definitely started
      return teamScoreDoc.exists();
    } catch (error) {
      console.error("Error checking if game started:", error);
      return false;
    }
  };

  // Mark that admin has joined the game
  const markAdminJoined = async (teamId) => {
    try {
      const gameStateRef = doc(db, "teams", teamId, "currentGame", "gameState");
      await setDoc(gameStateRef, {
        adminJoined: true,
        adminJoinedAt: new Date().toISOString()
      }, { merge: true });
      console.log("Admin marked as joined");
    } catch (error) {
      console.error("Error marking admin as joined:", error);
    }
  };

  // Handle join quiz button click
  const handleJoinQuiz = async () => {
    if (!userTeam) {
      alert("Please join or create a team first");
      return;
    }
    
    if (loadingTeam) {
      return; // Still loading team data
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Please sign in to join the quiz");
      return;
    }
    
    // Check if user is admin
    const isAdmin = userTeam.createdBy === currentUser.uid || 
                    userTeam.teamMembers?.find(m => m.id === currentUser.uid)?.role === 'admin';
    
    if (isAdmin) {
      // Admin can join immediately and mark as joined
      await markAdminJoined(userTeam.id);
      setActiveView('gameRounds');
    } else {
      // Team member - first check if they've confirmed their invitation
      const isConfirmed = await checkMemberConfirmed(userTeam.id, currentUser.uid);
      if (!isConfirmed) {
        alert("Confirm accepting the invite before joining the game. The invite is in the Chat from the Team Admin");
        return;
      }
      
      // Check if game has started
      const gameStarted = await checkGameStarted(userTeam.id);
      if (gameStarted) {
        setActiveView('gameRounds');
      } else {
        alert("Wait for team admin to join quiz");
      }
    }
  };

  // Auto-initialize quiz data on component mount
  useEffect(() => {
    const checkAndInitQuizData = async () => {
      try {
        // Check if quiz data exists
        const quizDoc = await getDoc(doc(db, "quiz", "musicTriviaQuestions"));
        
        if (!quizDoc.exists()) {
          console.log("Quiz data not found, initializing...");
          const result = await initializeQuizData();
          if (result.success) {
            console.log("Quiz data initialized successfully!");
          } else {
            console.error("Error initializing quiz data:", result.error);
          }
        } else {
          console.log("Quiz data already exists");
        }
      } catch (error) {
        console.error("Error checking quiz data:", error);
      }
    };

    checkAndInitQuizData();
  }, []); // Run once on mount

  const renderView = () => {
    switch(activeView) {
      case 'onboarding':
        return <GameOnboarding setTab={setTab} />;
      case 'teamCreation':
        return <TeamCreation clientObj={clientObj} onTeamCreated={handleTeamCreated} />;
      case 'teamInfo':
        return <TeamInfo 
          teamId={userTeam?.id}
          clientObj={clientObj}
          onBack={() => setActiveView('home')}
          onTeamDeleted={handleTeamDeleted}
        />;
      case 'gameRounds':
        return <GameRounds onBack={() => setActiveView('home')} clientObj={clientObj} />;
      case 'additionalRounds':
        return <AdditionalGameRounds />;
      case 'leaderboards':
        return <GameLeaderboards onBack={() => setActiveView('home')} />;
      case 'showdownAnswers':
        return <ShowdownAnswers
          onBack={() => setActiveView('home')}
          clientObj={clientObj}
          userObj={userObj}
        />;
      case 'pixelPuzzler':
        return <PixelPuzzler
          onBack={() => setActiveView('home')}
          clientObj={clientObj}
          userObj={userObj}
          jukebox={jukebox}
        />;
      case 'nameThatLyric':
        return <NameThatLyric
          onBack={() => setActiveView('home')}
          clientObj={clientObj}
          userObj={userObj}
        />;
      case 'nameThatTune':
        return <NameThatTune
          onBack={() => setActiveView('home')}
          clientObj={clientObj}
          userObj={userObj}
          scrollToJukebox={() => {
            // Scroll to jukebox or switch tab
            if (setTab) {
              setTab('jukebox');
            }
          }}
        />;
      default:
        return renderHomeView();
    }
  };

  const renderHomeView = () => (
    <div className="jukebox-showdown-home">
      <div className="game-home-container">
        <div className="jukebox-title">Jukebox Showdown</div>
        {/* User Header */}
        <div className="user-header">
          <div className="user-greeting">
            <div className="user-avatar">
              <img 
                src={userObj?.photoURL || "https://via.placeholder.com/40"} 
                alt="User Avatar"
              />
            </div>
            <div className="greeting-text">
              <span className="greeting">Good Afternoon!</span>
              <span className="username">{userObj?.displayName || "Player"}</span>
            </div>
          </div>
          <div className="diamonds-counter">
            <Gem className="diamond-icon" size={20} />
            <span className="diamond-count">{userDiamonds.toLocaleString()}</span>
            <span className="diamond-plus">+</span>
          </div>
        </div>

        {/* King of the Quiz Banner */}
        {/* <div className="king-banner-section">
          <div className="king-card">
            <div className="king-label">This week King of the Quiz</div>
            <div className="king-content">
              <div className="king-avatar">
                <img src="https://via.placeholder.com/60" alt="King" />
              </div>
              <div className="king-details">
                <h3 className="king-name">Maktum Talukdar</h3>
                <p className="king-score">12000 diamonds</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Game Options Grid */}
        <div className="game-options-grid">


          <button
            className="game-option-card scavenger-hunt"
            onClick={() => setActiveView('pixelPuzzler')}
          >
            <div className="option-icon"><Search size={24} /></div>
            <span className="option-label">Pixel Puzzler</span>
            {/** blurry picture gets less blurry after each letter is input  */}
          </button>


          <button
            className="game-option-card muuzbox-madness"
            onClick={() => setActiveView('showdownAnswers')}
          >
            <div className="option-icon"><Sparkles size={24} /></div>
            <span className="option-label">Jukebox Showdown</span>
            {/** question jobn screen bit red button answer by typing or shouting at host */}
          </button>          


          <button
            className="game-option-card name-that-lyric"
            onClick={() => setActiveView('nameThatLyric')}
          >
            <div className="option-icon"><Music size={24} /></div>
            <span className="option-label">Name That Lyric</span>
          </button>

          <button
            className="game-option-card name-that-tune"
            onClick={() => setActiveView('nameThatTune')}
          >
            <div className="option-icon"><Music2 size={24} /></div>
            <span className="option-label">Name That Tune</span>
          </button>


          {/* <button
            className="game-option-card leaderboard"
            onClick={() => setActiveView('leaderboards')}
          >
            <div className="option-icon"><Timer size={24} /></div>
            <span className="option-label">Name That Tune</span>

          </button> */}


          <button
            className="game-option-card leaderboard"
            onClick={() => setActiveView('leaderboards')}
          >
            <div className="option-icon"><BarChart3 size={24} /></div>
            <span className="option-label">Leaderboard</span>
          </button>

          {userTeam ? (
            <button 
              className="game-option-card team-name"
              onClick={() => setActiveView('teamInfo')}
            >
              <div className="option-icon">{userTeam.teamAvatar || <Users size={24} />}</div>
              <span className="option-label">{userTeam.teamName}</span>
            </button>
          ) : (
            <button 
              className="game-option-card create-team"
              onClick={() => setActiveView('teamCreation')}
            >
              <div className="option-icon"><Users size={24} /></div>
              <span className="option-label">Create Team</span>
            </button>
          )}



          {/* <button
            className="game-option-card achievements"
            onClick={() => setActiveView('additionalRounds')}
          >
            <div className="option-icon"><Trophy size={24} /></div>
            <span className="option-label">Achievements</span>
          </button> */}


        </div>

        {/* Daily Quiz Section */}
        <div className="daily-quiz-section">
          <div className="daily-quiz-card">
            <div className="quiz-content">
              <div className="quiz-text">
                <h3 className="quiz-title">Daily Quiz</h3>
                <p className="quiz-subtitle">Play, earn, compete</p>
              </div>
              <div className="quiz-graphic">
                <div className="question-bubbles">
                  <span className="bubble"><HelpCircle size={20} /></span>
                  <span className="bubble purple"><HelpCircle size={20} /></span>
                  <span className="bubble orange"><HelpCircle size={20} /></span>
                </div>
              </div>
            </div>
            <button 
              className="join-quiz-btn"
              onClick={handleJoinQuiz}
            >
              Join a quiz
            </button>
            
            {/* Show scores if game is completed */}
            {gameCompleted && completedGameScores && (
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-xl backdrop-blur-sm border border-purple-500/30">
                <div className="text-center mb-4">
                  <h4 className="text-xl font-bold text-yellow-400 mb-2 flex items-center justify-center gap-2">
                    <Trophy size={24} className="text-yellow-400" />
                    Quiz Complete!
                  </h4>
                  <div className="text-2xl font-bold text-white">
                    Team Score: {completedGameScores.teamScore} points
                  </div>
                </div>
                
                {/* Individual member scores */}
                <div className="space-y-3 mt-4">
                  {Object.entries(completedGameScores.members).map(([memberId, memberData]) => (
                    <div key={memberId} className="flex items-center gap-3 bg-purple-800/30 rounded-lg p-2">
                      {/* Member avatar */}
                      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-purple-500">
                        {memberData.photoURL ? (
                          <img 
                            src={memberData.photoURL} 
                            alt={memberData.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold">
                            {(memberData.displayName || "?")[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      
                      {/* Member name and score */}
                      <div className="flex-1">
                        <span className="text-white font-medium">
                          {memberData.displayName || "Team Member"}
                        </span>
                      </div>
                      <div className="text-cyan-300 font-bold">
                        {memberData.score || 0} pts
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Browse Smart Section */}
        {/* <div className="browse-section">
          <div className="browse-header">
            <h3 className="browse-title">Browse Smart!</h3>
            <button className="browse-arrow"><ArrowRight size={20} /></button>
          </div>
          <div className="browse-cards">
            <div className="browse-card purple">
              <span className="card-icon"><Music size={24} /></span>
              <span className="card-label">Music Trivia</span>
            </div>
            <div className="browse-card orange">
              <span className="card-icon"><Music2 size={24} /></span>
              <span className="card-label">Artist Quiz</span>
            </div>
          </div>
        </div> */}

        {/* Bottom Navigation */}
        {/* <div className="game-nav-buttons">
          <button 
            className="nav-btn"
            onClick={() => setActiveView('onboarding')}
          >
            How to Play
          </button>
          <button 
            className="nav-btn"
            onClick={() => setActiveView('teamCreation')}
          >
            Create Team
          </button>
        </div> */}
      </div>
    </div>
  );

  // Don't render if user is anonymous (they'll be redirected)
  if (isAnonymous) {
    return null;
  }

  return (
    <div className="jukebox-showdown-game backdrop-blur-sm  p-0">
      {renderView()}
    </div>
  );
};